import type { VmRecord, VmState } from "@/api/generated";

export type VmUiState =
  | "running"
  | "booting"
  | "stopping"
  | "stopped"
  | "failed";

export interface VmRailGroup {
  key: VmUiState | "snapshots" | "templates";
  label: string;
  count: number;
  disabled: boolean;
  vms: VmRecord[];
}

export interface VmPoolSummary {
  total: number;
  running: number;
  booting: number;
  stopped: number;
  failed: number;
  allocatedGpus: number;
}

export function uiStateForVm(state: VmState): VmUiState {
  switch (state) {
    case "pending":
    case "provisioning":
      return "booting";
    case "running":
      return "running";
    case "stopping":
      return "stopping";
    case "stopped":
      return "stopped";
    case "failed":
      return "failed";
  }
}

export function groupVmsForRail(vms: VmRecord[]): VmRailGroup[] {
  const running = vms.filter((vm) => uiStateForVm(vm.state) === "running");
  const booting = vms.filter((vm) => uiStateForVm(vm.state) === "booting");
  const stopped = vms.filter((vm) => uiStateForVm(vm.state) === "stopped");
  const failed = vms.filter((vm) => uiStateForVm(vm.state) === "failed");

  return [
    {
      key: "running",
      label: "running",
      count: running.length,
      disabled: false,
      vms: running,
    },
    {
      key: "booting",
      label: "booting",
      count: booting.length,
      disabled: false,
      vms: booting,
    },
    {
      key: "stopped",
      label: "stopped",
      count: stopped.length,
      disabled: false,
      vms: stopped,
    },
    {
      key: "failed",
      label: "failed",
      count: failed.length,
      disabled: false,
      vms: failed,
    },
    {
      key: "snapshots",
      label: "snapshots",
      count: 0,
      disabled: true,
      vms: [],
    },
    {
      key: "templates",
      label: "templates",
      count: 0,
      disabled: true,
      vms: [],
    },
  ];
}

export function summarizeVmPool(vms: VmRecord[]): VmPoolSummary {
  return {
    total: vms.length,
    running: vms.filter((vm) => uiStateForVm(vm.state) === "running").length,
    booting: vms.filter((vm) => uiStateForVm(vm.state) === "booting").length,
    stopped: vms.filter((vm) => uiStateForVm(vm.state) === "stopped").length,
    failed: vms.filter((vm) => uiStateForVm(vm.state) === "failed").length,
    allocatedGpus: vms
      .filter(
        (vm) =>
          uiStateForVm(vm.state) === "running" ||
          uiStateForVm(vm.state) === "booting",
      )
      .reduce((sum, vm) => sum + vm.spec.gpu_count, 0),
  };
}

export function pillForVmState(state: VmState): string {
  switch (uiStateForVm(state)) {
    case "running":
      return "ok";
    case "booting":
    case "stopping":
      return "degraded";
    case "failed":
      return "down";
    case "stopped":
      return "unknown";
  }
}
